import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../api/api';

const EventsAdmin = ({ showNotification }) => {
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'Admin';
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState({
    name: '',
    visibility: 'public',
    group_ids: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [evts, grps] = await Promise.all([
          api.getEvents(),
          api.getGroups(),
        ]);
        if (cancelled) return;
        const visibleEvents = isAdmin
          ? evts
          : evts.filter(e => e.status !== 'archived');
        setEvents(visibleEvents);
        setGroups(grps);
      } catch (e) {
        showNotification?.('Failed to load events', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showNotification, isAdmin]);

  // no-op

  const saveNew = async () => {
    if (!creating.name.trim()) return;
    const res = await api.createEvent(creating);
    if (res.success) {
      showNotification?.('Event created', 'success');
      setCreating({ name: '', visibility: 'public', group_ids: [] });
      // refresh list
      try {
        const evts = await api.getEvents();
        setEvents(isAdmin ? evts : evts.filter(e => e.status !== 'archived'));
      } catch {}
    } else showNotification?.(res.message || 'Failed to create', 'error');
  };

  const updateEvent = async (id, patch) => {
    const res = await api.updateEvent(id, patch);
    if (res.success) {
      showNotification?.('Event updated', 'success');
      try {
        const evts = await api.getEvents();
        setEvents(isAdmin ? evts : evts.filter(e => e.status !== 'archived'));
      } catch {}
    } else showNotification?.(res.message || 'Failed to update', 'error');
  };

  const deleteEvent = async id => {
    if (!window.confirm('Delete this event? It must be empty.')) return;
    const res = await api.deleteEvent(id);
    if (res.success) {
      showNotification?.('Event deleted', 'success');
      try {
        const evts = await api.getEvents();
        setEvents(isAdmin ? evts : evts.filter(e => e.status !== 'archived'));
      } catch {}
    } else showNotification?.(res.message || 'Failed to delete', 'error');
  };

  const clearEventTemplates = async evt => {
    if (!evt || !evt.id) return;
    if (
      !window.confirm(
        `Delete ALL templates in event "${evt.name}"? This cannot be undone.`
      )
    )
      return;
    try {
      const byEvent = await api.getTemplatesByEvent(evt.id);
      const templates = [
        ...(Array.isArray(byEvent?.global) ? byEvent.global : []),
        ...(Array.isArray(byEvent?.user) ? byEvent.user : []),
      ];
      const ids = templates.map(t => t.id).filter(Boolean);
      if (ids.length === 0) {
        showNotification?.('No templates found for this event.', 'info');
        return;
      }
      if (
        !window.confirm(
          `Confirm deletion of ${ids.length} template(s) in "${evt.name}"?`
        )
      )
        return;
      await api.bulkDeleteTemplates(ids);
      showNotification?.(
        `Deleted ${ids.length} template(s) from "${evt.name}"`,
        'success'
      );
    } catch (e) {
      showNotification?.(
        e?.message || 'Failed to delete event templates',
        'error'
      );
    }
  };

  if (loading) return <p>Loading events...</p>;

  const GroupSelector = ({ selected = [], onChange, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      const onDoc = e => {
        if (!ref.current) return;
        if (!ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }, []);
    const count = selected.length;
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          disabled={disabled}
          className={`px-3 py-2 text-xs rounded border ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white hover:bg-gray-50 text-gray-700'} inline-flex items-center justify-between w-full`}
          onClick={() => !disabled && setOpen(v => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {count === 0 ? 'Select groups' : `${count} selected`}
          <span className="ml-2 text-gray-400">▾</span>
        </button>
        {open && (
          <div className="absolute z-20 mt-2 w-64 bg-white border rounded shadow-lg p-2">
            <div className="max-h-48 overflow-auto divide-y">
              {groups.map(g => (
                <label
                  key={g.id}
                  className="flex items-center justify-between py-2 px-2 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <span className="truncate pr-2">{g.name}</span>
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={selected.includes(g.id)}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...selected, g.id]
                        : selected.filter(id2 => id2 !== g.id);
                      onChange(next);
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                className="text-xs text-gray-600 hover:text-gray-900"
                onClick={() => onChange([])}
              >
                Clear
              </button>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-white border rounded">
        <h4 className="font-medium mb-3">Create Event</h4>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-500">Name</label>
            <input
              className="form-input"
              placeholder="Name"
              value={creating.name}
              onChange={e =>
                setCreating(prev => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Permissions</label>
            <select
              className="form-select"
              value={creating.visibility}
              onChange={e =>
                setCreating(prev => ({ ...prev, visibility: e.target.value }))
              }
            >
              <option value="public">Public</option>
              <option value="group">Restricted</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-500">Permission Groups</label>
            <GroupSelector
              id="create"
              selected={creating.group_ids}
              onChange={next =>
                setCreating(prev => ({ ...prev, group_ids: next }))
              }
              disabled={creating.visibility !== 'group'}
            />
          </div>
          <div className="flex justify-end items-center">
            <button
              className="px-3 py-2 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={saveNew}
            >
              Create
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {events.map(evt => (
          <div
            key={evt.id}
            className="p-4 bg-gray-50 border rounded grid grid-cols-6 gap-3 items-center"
          >
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Name</label>
              <input
                className="form-input"
                value={evt.name}
                onChange={e =>
                  setEvents(prev =>
                    prev.map(ev =>
                      ev.id === evt.id ? { ...ev, name: e.target.value } : ev
                    )
                  )
                }
                onBlur={e => updateEvent(evt.id, { name: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <label className="text-xs text-gray-500">Status</label>
              <select
                className="form-select"
                value={evt.status}
                onChange={e => updateEvent(evt.id, { status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="text-xs text-gray-500">Permissions</label>
              <select
                className="form-select"
                value={evt.visibility}
                onChange={e =>
                  updateEvent(evt.id, { visibility: e.target.value })
                }
              >
                <option value="public">Public</option>
                <option value="group">Restricted</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="text-xs text-gray-500">Permission Groups</label>
              <GroupSelector
                id={`evt-${evt.id}`}
                selected={evt.group_ids || []}
                disabled={evt.visibility !== 'group'}
                onChange={next => {
                  setEvents(prev =>
                    prev.map(ev =>
                      ev.id === evt.id ? { ...ev, group_ids: next } : ev
                    )
                  );
                  updateEvent(evt.id, { group_ids: next });
                }}
              />
            </div>
            <div className="col-span-1 flex justify-end items-center gap-2">
              <button
                className="px-3 py-2 text-xs rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                onClick={() => clearEventTemplates(evt)}
              >
                Clear Templates
              </button>
              <button
                className="px-3 py-2 text-xs rounded bg-red-100 text-red-600 hover:bg-red-200"
                onClick={() => deleteEvent(evt.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventsAdmin;
